import React, { useState, useEffect, CSSProperties } from 'react';
import * as Sentry from "@sentry/react";
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';

// Hooks and Utils
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import useOrganizationV2 from 'Hooks/useOrganizationV2';
import useAppView from 'Hooks/useAppView';
import useUser from 'Hooks/useUser';
import { goToUrl, showToast } from 'modules/Utils';

// API
import { getBillingDetails_api, BillingDetailsResponse, cancelSubscriptionV2_api, resumeSubscriptionV2_api, getBillingPortalUrl_api } from 'api/paymentsV2Api';


// Components
import { Container, StyledLabelP } from 'modules/Sty';
import ButtonSymphony from 'elements/ButtonSymphony';
import AvatarUserStatus from 'components/shareable/AvatarUserStatus';
import CurrencyDropdown from 'components/shareable/CurrencyDropdown';
import CancellationConfirmationModal from 'components/shareable/CancellationConfirmationModal/CancellationConfirmationModal';

import EditSpendLimitModal from 'pages/MarketingPage/Components/Modals/AdminEditSpendLimitModal';

// Constants and Types
import { CURRENCY_SYMBOLS, DEFAULT_CURRENCY, PlanType } from 'modules/Const';
import getSymbolFromCurrency from 'currency-symbol-map';



/** Billing context type for unified logic */
type BillingEntityType = 'brand' | 'organization' | 'free';

interface BillingContext {
    entityType: BillingEntityType;
    entityId: number | null;
    entitySlug: string | null;
    billingData: BillingDetailsResponse | null;
    isLoading: boolean;
}

/**
 * Unified billing component that handles all billing scenarios:
 * - Free brands (not in organization)
 * - Paid brands (not in organization) 
 * - Brands within organizations (shows organization billing)
 * - Organization-only view
 */
const BillingTabV2: React.FC = () => {
    const history = useHistory();
    const { currentBrand, isPaidBrand } = useBrand() as BrandExistsState;
    const { organization } = useOrganizationV2();
    const { isTeamView } = useAppView();
    const { isSymphonyAdmin: isAdmin } = useUser();

    // Billing context state
    const [billingContext, setBillingContext] = useState<BillingContext>({
        entityType: 'free',
        entityId: null,
        entitySlug: null,
        billingData: null,
        isLoading: false
    });

    // UI state
    const [showCancellationModal, setShowCancellationModal] = useState(false);
    const [isCancelling, setIsCancelling] = useState(false);
    const [isResuming, setIsResuming] = useState(false);
    const [isEditSpendLimitModalOpen, setIsEditSpendLimitModalOpen] = useState(false);

    const currencyCode = currentBrand?.currency?.code || DEFAULT_CURRENCY;
    const currencySymbol = getSymbolFromCurrency(currencyCode);

    /**
     * Determines the billing context based on current state
     */
    const determineBillingContext = (): BillingContext => {
        // If in team view and has organization, always show organization billing
        if (isTeamView() && organization?.id) {
            return {
                entityType: 'organization',
                entityId: organization.id,
                entitySlug: null, // Organizations don't have slugs
                billingData: null,
                isLoading: false
            };
        }

        // If brand belongs to organization (has organization_id), show organization billing
        if (currentBrand?.organizationId && organization?.id) {
            return {
                entityType: 'organization',
                entityId: organization.id,
                entitySlug: null,
                billingData: null,
                isLoading: false
            };
        }

        // If brand is paid but not in organization, show brand billing
        if (isPaidBrand && currentBrand?.id) {
            return {
                entityType: 'brand',
                entityId: currentBrand.id,
                entitySlug: currentBrand.slug,
                billingData: null,
                isLoading: false
            };
        }

        // Default to free brand billing
        return {
            entityType: 'free',
            entityId: currentBrand?.id || null,
            entitySlug: currentBrand?.slug || null,
            billingData: null,
            isLoading: false
        };
    };

    /**
     * Fetches billing data based on current context
     */
    const fetchBillingData = async (context: BillingContext): Promise<void> => {
        if (!context.entityId || context.entityType === 'free') {
            return;
        }

        setBillingContext(prev => ({ ...prev, isLoading: true }));

        try {
            const billingData = await getBillingDetails_api({
                brandId: context.entityType === 'brand' ? context.entityId : undefined,
                organizationId: context.entityType === 'organization' ? context.entityId : undefined,
                currency: currencyCode,
                includeUsageData: true
            });

            setBillingContext(prev => ({
                ...prev,
                billingData,
                isLoading: false
            }));
        } catch (error) {
            console.error('Error fetching billing data:', error);
            setBillingContext(prev => ({ ...prev, isLoading: false }));
        }
    };



    /**
     * Redirects to unified billing portal
     */
    const redirectToBillingPortal = async (): Promise<void> => {
        try {
            setBillingContext(prev => ({ ...prev, isLoading: true }));

            let params = {

            }

            if (billingContext.entityType === 'organization') {
                params = {
                    organizationId: billingContext.entityId || undefined
                }
            } else {
                params = {
                    brandId: billingContext.entityId || undefined
                }
            }

            // Use unified billing portal API
            let url = await getBillingPortalUrl_api(params);


            goToUrl(url, () => setBillingContext(prev => ({ ...prev, isLoading: false })));
        } catch (error) {
            setBillingContext(prev => ({ ...prev, isLoading: false }));
            Sentry.captureException(error);
            console.error('Error redirecting to billing portal:', error);
            showToast({
                message: 'Failed to open billing portal. Please try again.',
                error: true
            });
        }
    };

    /**
     * Handles subscription cancellation
     */
    const handleCancellationConfirm = async (): Promise<void> => {
        if (!billingContext.entityId) return;

        setIsCancelling(true);
        try {
            let params = {};

            if (billingContext.entityType === 'organization') {
                params = {
                    organizationId: billingContext.entityId || undefined
                };
            } else {
                params = {
                    brandId: billingContext.entityId || undefined
                };
            }

            const result = await cancelSubscriptionV2_api(params);
            setShowCancellationModal(false);

            if (result.success) {
                showToast({
                    message: 'Subscription cancelled successfully!',
                    error: false
                });
            }

            // Refresh billing data
            const context = determineBillingContext();
            await fetchBillingData(context);
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error cancelling subscription:', error);
        } finally {
            setIsCancelling(false);
        }
    };

    /**
     * Handles subscription resumption
     */
    const handleResumeSubscription = async (): Promise<void> => {
        if (!billingContext.entityId) return;

        setIsResuming(true);
        try {
            let params = {};

            if (billingContext.entityType === 'organization') {
                params = {
                    organizationId: billingContext.entityId || undefined
                };
            } else {
                params = {
                    brandId: billingContext.entityId || undefined
                };
            }

            await resumeSubscriptionV2_api(params);

            // Refresh billing data
            const context = determineBillingContext();
            await fetchBillingData(context);

            showToast({
                message: 'Subscription resumed successfully!',
                error: false
            });
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error resuming subscription:', error);
            showToast({
                message: 'Failed to resume subscription. Please try again.',
                error: true
            });
        } finally {
            setIsResuming(false);
        }
    };

    /**
     * Opens admin links in new tab
     */
    const openAdminLink = (url?: string): void => {
        if (url) {
            window.open(url, '_blank');
        }
    };

    /**
     * Gets display name for plan type
     */
    const getPlanDisplayName = (planType: string): string => {
        switch (planType) {
            case 'LITE': return 'Lite';
            case 'PRO': return 'Pro';
            case 'STARTER': return 'Starter';
            case 'GROWTH': return 'Growth';
            case 'TEAM_V2': return 'Team';
            case 'TEAM': return 'Team';
            case 'organization': return 'Team';
            default: return planType;
        }
    };

    /**
     * Renders a column with title and subtitle
     */
    const renderColumn = (title: string, subtitle: string, className?: string, subtitleColor?: string) => (
        <div className={`column ${className || ''}`} style={sty.column}>
            <StyledLabelP fontSize={16}>{title}</StyledLabelP>
            <StyledLabelP marginTop="4px" fontSize={24} color={subtitleColor}>
                {subtitle}
            </StyledLabelP>
        </div>
    );

    /**
     * Renders data freshness indicator
     */
    const renderDataFreshnessIndicator = (): JSX.Element | null => {
        if (!billingContext.billingData?.dataFreshness) return null;

        const { dataFreshness, cachedAt } = billingContext.billingData;
        const cacheTime = cachedAt ? dayjs(cachedAt).fromNow() : '';

        let indicatorColor = '#10B981'; // green for live
        let statusText = 'Live data';

        if (dataFreshness === 'cached') {
            indicatorColor = '#F59E0B'; // amber for cached
            statusText = `Cached data (${cacheTime})`;
        } else if (dataFreshness === 'stale') {
            indicatorColor = '#EF4444'; // red for stale
            statusText = `Stale data (${cacheTime})`;
        }

        return (
            <Container
                className="dataFreshnessIndicator"
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="8px"
                padding="8px 0px"
            >
                <div
                    style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: indicatorColor
                    }}
                />
                <StyledLabelP fontSize={12} fontWeight={400} color="#6B7280">
                    {statusText}
                </StyledLabelP>
            </Container>
        );
    };

    /**
     * Renders plan information section
     */
    const renderPlanSection = (): JSX.Element => {
        const { billingData } = billingContext;

        if (billingContext.entityType === 'free') {
            return (
                <Container
                    className="planSection"
                    display="flex"
                    flexDirection="column"
                    padding="24px 0px"
                    borderBottom="1px solid #E5E7EB"
                >
                    <StyledLabelP fontSize={16}>Plan</StyledLabelP>
                    <StyledLabelP marginTop="4px" fontSize={24}>Free</StyledLabelP>
                </Container>
            );
        }

        if (!billingData) {
            return (
                <Container
                    className="planSectionLoading"
                    display="flex"
                    flexDirection="column"
                    padding="24px 0px"
                    borderBottom="1px solid #E5E7EB"
                >
                    <StyledLabelP fontSize={16}>Loading billing information...</StyledLabelP>
                </Container>
            );
        }

        const planName = getPlanDisplayName(billingData.planType);
        const cycleText = billingData.billingCycle === 'monthly' ? 'Monthly' : 'Annual';

        // Determine what to show for the second column
        let secondColumnTitle = '';
        let secondColumnValue = '';

        if (billingData.scheduledCancellationDate) {
            secondColumnTitle = 'Plan Cancels On';
            secondColumnValue = dayjs(billingData.scheduledCancellationDate).format('MMM DD, YYYY');
        } else if (billingData.nextBillingDate) {
            secondColumnTitle = 'Next Billing Date';
            secondColumnValue = dayjs(billingData.nextBillingDate).format('MMM DD, YYYY');
        }

        // Determine what to show for the third column (trial end date)
        let thirdColumnTitle = '';
        let thirdColumnValue = '';

        if (billingData.trialEndDate) {
            thirdColumnTitle = 'Trial End Date';
            thirdColumnValue = dayjs(billingData.trialEndDate).format('MMM DD, YYYY');
        }

        const isForcedByAdmin = currentBrand?.subscription?.type === 'forced';
        return (
            <div className="planSection">
                {renderDataFreshnessIndicator()}

                <div className="planDetailsRow" style={sty.planDetailsRow}>
                    {renderColumn('Current Plan', `${planName} (${cycleText})`, 'currentPlanColumn')}
                    {secondColumnTitle && renderColumn(secondColumnTitle, secondColumnValue, 'nextBillingColumn')}
                    {thirdColumnTitle && renderColumn(thirdColumnTitle, thirdColumnValue, 'trialEndColumn')}
                    {isForcedByAdmin && renderColumn('Discounts', 'An admin has upgraded you for free!', 'forcedByAdminColumn')}
                </div>
            </div>
        );
    };

    /**
     * Renders usage information (seats, brands, etc.)
     */
    const renderUsageSection = (): JSX.Element | null => {
        const { billingData } = billingContext;
        if (!billingData || billingContext.entityType === 'free') return null;

        // Prepare seats data
        let seatsData = null;
        if (billingData.seats) {
            const { totalSeatsUsed, seatsLimit, seatsOverage, seatsOverFee } = billingData.seats;
            seatsData = {
                title: 'Invited Users',
                value: `${totalSeatsUsed.toLocaleString('en')} / ${seatsLimit.toLocaleString('en')}`,
                overageTitle: seatsOverFee > 0 ? 'User Overage Fee' : null,
                overageValue: seatsOverFee > 0 ? `${currencySymbol}${seatsOverFee.toFixed(2)}/month` : null
            };
        }

        // Prepare brands data (for organizations)
        let brandsData = null;
        if (billingContext.entityType === 'organization' && billingData.brands) {
            const { totalBrandsUsed, brandsLimit, brandsOverage, brandsOverFee } = billingData.brands;
            brandsData = {
                title: 'Active Brands',
                value: `${totalBrandsUsed.toLocaleString('en')} / ${brandsLimit.toLocaleString('en')}`,
                overageTitle: brandsOverFee > 0 ? 'Brand Overage Fee' : null,
                overageValue: brandsOverFee > 0 ? `${currencySymbol}${brandsOverFee.toFixed(2)}/month` : null
            };
        }

        if (!seatsData && !brandsData) return null;

        return (
            <div className="usageSection">
                {/* Seats Row */}
                {seatsData && (
                    <div className="seatsRow" style={sty.usageRow}>
                        {renderColumn(seatsData.title, seatsData.value, 'invitedUsersColumn')}
                        {seatsData.overageTitle && seatsData.overageValue && renderColumn(seatsData.overageTitle, seatsData.overageValue, 'userOverageColumn')}
                    </div>
                )}

                {/* Brands Row */}
                {brandsData && (
                    <div className="brandsRow" style={sty.usageRow}>
                        {renderColumn(brandsData.title, brandsData.value, 'activeBrandsColumn')}
                        {brandsData.overageTitle && brandsData.overageValue && renderColumn(brandsData.overageTitle, brandsData.overageValue, 'brandOverageColumn')}
                    </div>
                )}
            </div>
        );
    };

    /**
     * Renders action buttons (billing portal, cancel/resume)
     */

    const renderCancelResumeButtons = (): JSX.Element | null => {
        const { billingData } = billingContext;
        if (!billingData) return null;

        console.log('billingData', billingData);
        console.log('currentBrand.subscription', currentBrand?.subscription);
        const isForcedPro = currentBrand?.subscription?.type === 'forced';

        if (isForcedPro) {
            return null;
        }

        if (billingData.scheduledCancellationDate) {
            return (
                <ButtonSymphony
                    className="resumeSubscriptionButton"
                    onClick={handleResumeSubscription}
                    isLoading={isResuming}
                    disabled={isResuming || billingContext.isLoading}
                >
                    Resume Subscription
                </ButtonSymphony>
            )
        } else {
            return (
                <ButtonSymphony
                    className="cancelSubscriptionButton"
                    onClick={() => setShowCancellationModal(true)}
                    variant="outlined"
                    color="red"
                    disabled={billingContext.isLoading}
                >
                    Cancel Subscription
                </ButtonSymphony>
            )
        }
    }
    const renderActionButtons = (): JSX.Element | null => {
        if (billingContext.entityType === 'free' && !currentBrand?.subscription?.allowCustomerPortal) {
            return null;
        }

        return (
            <Container
                className="actionButtons"
                display="flex"
                flexDirection="row"
                gap="16px"
                padding="24px 0px"
                borderBottom="1px solid #E5E7EB"
            >
                {/* Billing Portal Button */}
                <ButtonSymphony
                    className="billingPortalButton"
                    onClick={redirectToBillingPortal}
                    disabled={billingContext.isLoading}
                    isLoading={billingContext.isLoading}
                    variant="outlined"
                    loadingText="Loading..."
                >
                    {billingContext.entityType === 'free' ? 'View Billing Details' : 'View Billing Settings'}
                </ButtonSymphony>

                {/* Cancel/Resume Buttons */}
                {renderCancelResumeButtons()}
            </Container>
        );
    };

    /**
     * Renders admin controls
     */
    const renderAdminControls = (): JSX.Element | null => {
        const { billingData } = billingContext;
        if (!isAdmin || !billingData?.adminLinks) return null;

        return (
            <Container
                className="adminControls"
                display="flex"
                flexDirection="row"
                gap="16px"
                padding="24px 0px"
                borderBottom="1px solid #E5E7EB"
            >
                {billingData.adminLinks.viewSubscriptionUrl && (
                    <ButtonSymphony
                        className="adminViewSubscriptionButton"
                        onClick={() => openAdminLink(billingData.adminLinks?.viewSubscriptionUrl)}
                        variant="outlined"
                    >
                        Admin: View Stripe Subscription
                    </ButtonSymphony>
                )}

                {billingData.adminLinks.viewCustomerUrl && (
                    <ButtonSymphony
                        className="adminViewCustomerButton"
                        onClick={() => openAdminLink(billingData.adminLinks?.viewCustomerUrl)}
                        variant="outlined"
                    >
                        Admin: View Stripe Customer
                    </ButtonSymphony>
                )}
            </Container>
        );
    };



    /**
     * Renders currency dropdown for brand view
     */
    const renderCurrencyDropdown = (): JSX.Element | null => {
        if (billingContext.entityType === 'organization' || !currentBrand?.currency) {
            return null;
        }

        const currency = currentBrand.currency;
        return (
            <CurrencyDropdown
                initialCurrency={currency?.code
                    ? `${currency.code} (${CURRENCY_SYMBOLS[currency.code]})`
                    : `${DEFAULT_CURRENCY} (${CURRENCY_SYMBOLS[DEFAULT_CURRENCY]})`
                }
            />
        );
    };

    // Initialize billing context and load data
    useEffect(() => {
        const context = determineBillingContext();
        setBillingContext(context);

        if (context.entityType !== 'free') {
            fetchBillingData(context);
        }

    }, [currentBrand?.id, organization?.id, isTeamView(), isPaidBrand]);

    return (
        <Container className="billingTabV2">
            {/* Header */}
            <Container
                className="billingHeader"
                padding="0 0 24 0"
                borderBottom="1px solid #E5E7EB"
            >
                <StyledLabelP fontSize={20}>Billing</StyledLabelP>
            </Container>

            {/* User Status */}
            <Container
                className="userStatusContainer"
                padding="24 0"
                borderBottom="1px solid #E5E7EB"
            >
                <AvatarUserStatus
                    className="avatarUserStatusBillingTab"
                    isWhiteBorder
                    userContainerStyles={{ gap: 4 }}
                    styles={{ gap: 16 }}
                    userNameTextStyles={{ fontSize: 18 }}
                    proTextStyles={{ fontSize: 12, color: '#E08507' }}
                />
            </Container>

            {/* Currency Dropdown */}
            {renderCurrencyDropdown()}

            {/* Action Buttons */}
            {renderActionButtons()}

            {/* Admin Controls */}
            {renderAdminControls()}

            {/* Plan Information */}
            {renderPlanSection()}

            {/* Usage Information */}
            {renderUsageSection()}



            {/* Modals */}
            <CancellationConfirmationModal
                isOpen={showCancellationModal}
                onClose={() => setShowCancellationModal(false)}
                onConfirm={handleCancellationConfirm}
                currentPlan={currentBrand?.tier as PlanType}
                isGrandfathered={false} // TODO: Add grandfathered check
                isLoading={isCancelling}
            />

            {isAdmin && billingContext.entitySlug && (
                <EditSpendLimitModal
                    brandSlug={billingContext.entitySlug}
                    open={isEditSpendLimitModalOpen}
                    closeModal={() => setIsEditSpendLimitModalOpen(false)}
                    currentSpendLimit={billingContext.billingData?.usage?.spentToDate || 0}
                    defaultSpendLimit={billingContext.billingData?.usage?.spentToDate || 0}
                    updateSpendLimit={() => {
                        const context = determineBillingContext();
                        fetchBillingData(context);
                    }}
                />
            )}
        </Container>
    );
};

const sty: Record<string, CSSProperties> = {
    planDetailsRow: {
        display: 'flex',
        flexDirection: 'row',
        padding: '24px 0px',
        borderBottom: '1px solid #E5E7EB',
        gap: 64
    },
    usageRow: {
        display: 'flex',
        flexDirection: 'row',
        padding: '24px 0px',
        borderBottom: '1px solid #E5E7EB',
        gap: 64
    },
    column: {
        display: 'flex',
        flexDirection: 'column'
    }
};

export default BillingTabV2;
